import { useEffect, useState, useCallback } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable, Badge } from "@/components/common/DataTable";
import { slaService, tenantService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { PRIORITIES, PRIORITY_COLOR } from "./constants";

const EMPTY = { name: "", priority: "Medium", responseMins: 240, resolutionMins: 1440, businessHoursOnly: false, active: true, tenantId: "" };

function Field({ label, children }) {
  return <div><label className="block text-sm font-medium text-foreground mb-1">{label}</label>{children}</div>;
}
const fmtMins = (m) => (m >= 60 ? `${Math.round(m / 60)}h` : `${m}m`);

export default function SlaPoliciesPage() {
  const { hasPermission, user } = useAuth();
  const isSuper = user?.isSuperAdmin;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [tenants, setTenants] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await slaService.list({})).items || []); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isSuper) tenantService.list().then((d) => setTenants(d.items || [])).catch(() => {}); }, [isSuper]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...EMPTY, ...r }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (isSuper && !editing && !form.tenantId) return toast.error("Select a tenant");
    setSaving(true);
    try {
      const payload = { ...form, responseMins: Number(form.responseMins), resolutionMins: Number(form.resolutionMins) };
      if (editing) { delete payload.tenantId; await slaService.update(editing.id, payload); toast.success("SLA policy updated"); }
      else { if (!isSuper) delete payload.tenantId; await slaService.create(payload); toast.success("SLA policy created"); }
      setOpen(false); load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete SLA policy "${r.name}"?`)) return;
    try { await slaService.remove(r.id); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const canManage = hasPermission("SLA.MANAGE");
  const columns = [
    { key: "name", header: "Policy", render: (r) => <span className="font-medium text-foreground">{r.name}</span> },
    { key: "priority", header: "Priority", render: (r) => <Badge color={PRIORITY_COLOR[r.priority] || "slate"}>{r.priority}</Badge> },
    { key: "responseMins", header: "Response Target", render: (r) => fmtMins(r.responseMins) },
    { key: "resolutionMins", header: "Resolution Target", render: (r) => fmtMins(r.resolutionMins) },
    { key: "active", header: "Active", render: (r) => <Badge color={r.active ? "green" : "slate"}>{r.active ? "Active" : "Inactive"}</Badge> },
    {
      key: "_a", header: "",
      render: (r) => canManage && (
        <div className="flex items-center justify-end gap-1">
          <button onClick={() => openEdit(r)} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground" data-testid={`sla-edit-${r.id}`}><Pencil className="h-4 w-4" /></button>
          <button onClick={() => remove(r)} className="p-1.5 rounded-md hover:bg-destructive/10 text-destructive" data-testid={`sla-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      ),
    },
  ];

  return (
    <div data-testid="sla-policies-page">
      <PageHeader title="SLA Policies" subtitle="Response & resolution targets per priority"
        actions={canManage && (
          <button onClick={openCreate} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90" data-testid="sla-create-btn">
            <Plus className="h-4 w-4" /> New Policy
          </button>
        )}
      />
      <DataTable columns={columns} rows={rows} loading={loading} testid="sla-table" empty="No SLA policies" />

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-card text-foreground rounded-2xl w-full max-w-md p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="sla-dialog">
            <h3 className="text-lg font-bold mb-4">{editing ? "Edit" : "New"} SLA Policy</h3>
            <div className="space-y-3.5">
              {isSuper && !editing && (
                <Field label="Tenant *">
                  <select value={form.tenantId} onChange={(e) => setForm({ ...form, tenantId: e.target.value })} className="ciq-input" data-testid="sla-tenant">
                    <option value="">Select tenant…</option>
                    {tenants.map((t) => <option key={t.id} value={t.id}>{t.tenantName || t.name || t.id}</option>)}
                  </select>
                </Field>
              )}
              <Field label="Name *"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="ciq-input" data-testid="sla-name" /></Field>
              <Field label="Priority"><select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="ciq-input" data-testid="sla-priority">{PRIORITIES.map((p) => <option key={p}>{p}</option>)}</select></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Response (mins)"><input type="number" value={form.responseMins} onChange={(e) => setForm({ ...form, responseMins: e.target.value })} className="ciq-input" data-testid="sla-response" /></Field>
                <Field label="Resolution (mins)"><input type="number" value={form.resolutionMins} onChange={(e) => setForm({ ...form, resolutionMins: e.target.value })} className="ciq-input" data-testid="sla-resolution" /></Field>
              </div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active</label>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm rounded-lg border border-border hover:bg-muted">Cancel</button>
              <button onClick={save} disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 flex items-center gap-2 disabled:opacity-60" data-testid="sla-save">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
